import React, { useState } from "react";

import { Controller, useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage, RequiredMark } from "@components/inputs/shared";
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import useLocaleDateFormatForDatePicker from "@state/hooks/shared/useLocaleDateFormatForDatePicker";
import { getCurrentCulture } from "@utils";
import classNames from "classnames";
import { DateTime } from "luxon";

import { PopoverMessage } from ".";
import { DatePickerProps, LabelProps } from "./types";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
const ReactDatePicker = lazy(
	loadFailPath,
	//@ts-expect-error
	() => import("react-datetime-picker/dist/entry.nostyle")
);

export default function DatePicker(props: DatePickerProps) {
	const {
		name,
		className,
		errorMessage = undefined,
		required = false,
		disabled = false,
		calendarDisabled = false,
		readOnly = false,
		hasBorder = false,
		validate,
		format,
		disableClearIcon,
		...dateTimePickerProps
	} = props;

	const { t } = useTranslation();
	const { control } = useFormContext();
	const currentDateFormat = useLocaleDateFormatForDatePicker();

	const { errors } = useFormState();
	const error = errors?.name;

	const wrapperClassNames = classNames({
		"react-datetime-picker--readonly": readOnly,
		"react-datetime-picker--bordered": hasBorder,
		"react-datetime-picker--error": error,
	});

	return (
		<div className={className}>
			<Label data={props} />
			<ErrorMessage name={name} />
			<Controller
				name={name}
				control={control}
				rules={{
					required: {
						value: required,
						message: errorMessage || "COMMON.REQUIRED_FIELD",
					},
					validate: validate,
				}}
				render={({ field }) => (
					<ReactDatePicker
						className={wrapperClassNames}
						value={
							field.value
								? DateTime.fromISO(field.value).toJSDate()
								: undefined
						}
						onChange={(date: any) =>
							field.onChange(date ? date.toISOString() : "")
						}
						locale={getCurrentCulture()}
						dayPlaceholder={t("COMMON.DAY_PLACEHOLDER")}
						monthPlaceholder={t("COMMON.MONTH_PLACEHOLDER")}
						yearPlaceholder={t("COMMON.YEAR_PLACEHOLDER")}
						format={format || currentDateFormat}
						disableCalendar={calendarDisabled}
						clearIcon={
							disabled || disableClearIcon ? null : undefined
						}
						disabled={disabled}
						{...dateTimePickerProps}
					/>
				)}
			/>
		</div>
	);
}

function Label(props: LabelProps) {
	const { t } = useTranslation();

	const [mouseOverState, setMouseOverState] = useState(false);
	if (!props.data.label) {
		return null;
	}
	return (
		<label
			className={classNames("label", props.data.labelClassName)}
			onMouseEnter={() => setMouseOverState(true)}
			onMouseLeave={() => setMouseOverState(false)}
		>
			{t(props.data.label)}{" "}
			<RequiredMark required={props.data.required} />
			<PopoverMessage
				popoverMsg={props.data.popoverMsg}
				mouseOverState={mouseOverState}
			/>
		</label>
	);
}
